// SceneImagePreview.jsx
// This component is used to display a preview of a scene image in the file viewer.

// Copyright HS Analysis GmbH, 2025
// Authors: Jakob Burghardt, Viktor Eberhardt

// Framework imports
import React from "react";
import PropTypes from "prop-types";

// MUI imports
import { LazyLoadImage } from "react-lazy-load-image-component";
import { makeStyles, useTheme } from "@mui/styles";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

// HSA Imports
import Backend from "../../common/utils/Backend";

const useStyles = makeStyles(() => ({
  imgWrapper: {
    display: "flex",
    position: "relative",
    width: "100%",
    flexDirection: "column",
    alignItems: "center",
    border: "1px solid #ddd",
    borderRadius: 4,
    padding: 5,
    backgroundColor: "#f9f9f9",
    pointerEvents: "none",
  },
  imgLabel: {
    fontSize: 12,
    marginTop: 5,
    color: "#555",
    wordWrap: "break-word",
    width: "100%",
    maxWidth: "190px",
    overflow: "hidden",
  },
  sceneAdditionalName: {
    fontSize: 12,
    marginTop: 5,
    color: "#888",
    wordWrap: "break-word",
    width: "100%",
    maxWidth: "190px",
    overflow: "hidden",
  },
  sceneComment: {
    fontSize: 12,
    marginTop: 5,
    color: "#888",
    wordWrap: "break-word",
    width: "100%",
    maxWidth: "190px",
    overflow: "hidden",
  },
  img: {
    display: "inline-block",
    cursor: "pointer",
    width: "100%",
    maxHeight: "200px",
    objectFit: "contain",
  },
  imageContainer: {
    position: "relative",
    width: "100%",
    height: 200,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  imageOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
  },
  placeholder: {
    height: 200,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  lazyImage: {
    objectFit: "contain",
    width: "100%",
  },
  ellipsisText: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "block",
    alignItems: "center",
    maxWidth: "150px",
    position: "relative",
    paddingRight: "16px",
    // Show the info icon automatically when this text is truncated
    '&[data-truncated="true"] $infoIcon': {
      visibility: "visible",
    },
  },
  infoIcon: {
    position: "absolute",
    right: 0,
    top: 2,
    marginLeft: 4,
    fontSize: 16,
    color: "#888",
    padding: 0,
    pointerEvents: "auto",
    visibility: "hidden",
  },
  hasSeenDot: {
    position: "absolute",
    top: 5,
    right: 5,
    width: 10,
    height: 10,
    borderRadius: "50%",
  },
}));

const SceneImagePreview = ({
  scene,
  width,
  showAdditionalName,
  showComments,
  fileStatus,
  isTruncated,
  setDialogTitle,
  setDialogText,
  setDialogOpen,
  handleImageError,
  scrollContainer,
  useAdditionalNameInsteadOfFilename = false,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const wrapperRef = React.useRef(null);
  const additionalNameRef = React.useRef(null);
  const commentRef = React.useRef(null);
  const [shouldLoad, setShouldLoad] = React.useState(false);
  const [mainLoaded, setMainLoaded] = React.useState(false);
  const [mainError, setMainError] = React.useState(false);
  let fileStatusBorder = "";
  if (fileStatus === "imgActive") {
    fileStatusBorder = `solid 3px ${theme.palette.primary.main}`;
  } else if (fileStatus === "imgSplitScreen") {
    fileStatusBorder = `dashed 3px ${theme.palette.primary.main}`;
  }

  // Observe visibility within the provided scroll container (or window if none) and only load image when visible
  React.useEffect(() => {
    if (shouldLoad) return; // already loaded
    const rootEl =
      typeof scrollContainer === "string"
        ? document.querySelector(scrollContainer)
        : scrollContainer || null;
    const root = rootEl instanceof Element ? rootEl : null;
    const el = wrapperRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShouldLoad(true);
          }
        });
      },
      { root, threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [scrollContainer, shouldLoad]);

  // Track image load state for spinner overlay
  const mainSrc = React.useMemo(
    () => Backend.renderThumbnail(encodeURIComponent(scene.id)),
    [scene?.id]
  );
  React.useEffect(() => {
    setMainLoaded(false);
    setMainError(false);
  }, [mainSrc]);
  const mainLoading = shouldLoad && !mainLoaded && !mainError;

  // Helper to (re)compute truncation state and set data attribute
  const updateTruncation = React.useCallback(
    (el) => {
      if (!el) return;
      try {
        if (isTruncated(el)) el.setAttribute("data-truncated", "true");
        else el.removeAttribute("data-truncated");
      } catch (_) {
        // no-op: defensive
      }
    },
    [isTruncated]
  );

  // Re-evaluate truncation on width/text changes
  React.useEffect(() => {
    updateTruncation(additionalNameRef.current);
    updateTruncation(commentRef.current);
  }, [width, scene?.additionalName, scene?.comment, updateTruncation]);

  return (
    <div className={classes.imgWrapper} ref={wrapperRef}>
      {showAdditionalName &&
        scene.additionalName &&
        scene.additionalName !== "" &&
        !useAdditionalNameInsteadOfFilename && (
          <div
            className={`${classes.sceneAdditionalName} ${classes.ellipsisText}`}
            title={scene.additionalName}
            ref={(el) => {
              additionalNameRef.current = el;
              updateTruncation(el);
            }}
          >
            {scene.additionalName}
            <span>
              <IconButton
                className={classes.infoIcon}
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setDialogTitle("Additional Name");
                  setDialogText(scene.additionalName);
                  setDialogOpen(true);
                }}
                tabIndex={-1}
              >
                <InfoOutlinedIcon fontSize="inherit" />
              </IconButton>
            </span>
          </div>
        )}
      <div className={classes.imgLabel}>
        {useAdditionalNameInsteadOfFilename
          ? scene.additionalName || scene.path.split(/[/\\]/).pop()
          : scene.path.split(/[/\\]/).pop()}
        {scene.sceneCount > 1 && ", S" + (scene.scene + 1)}
      </div>
      <div className={classes.imageContainer} style={{ width: width }}>
        {shouldLoad ? (
          <LazyLoadImage
            key={scene.path + scene.id + "lazy"}
            height={200}
            placeholder={
              <div className={classes.placeholder}>
                <CircularProgress />
              </div>
            }
            src={mainSrc}
            alt="Preview Image"
            className={classes.img}
            style={{ border: fileStatusBorder, width: "100%" }}
            threshold={0.15}
            onLoad={() => {
              setMainLoaded(true);
              setMainError(false);
            }}
            onError={() => {
              setMainError(true);
              handleImageError(scene);
            }}
          />
        ) : (
          <div
            className={classes.placeholder}
            style={{ height: 200, width: "100%" }}
          >
            <CircularProgress />
          </div>
        )}
        {mainLoading && (
          <div className={classes.imageOverlay}>
            <CircularProgress />
          </div>
        )}
      </div>
      {showComments && scene.comment && scene.comment !== "" && (
        <div
          className={`${classes.sceneComment} ${classes.ellipsisText}`}
          title={scene.comment}
          ref={(el) => {
            commentRef.current = el;
            updateTruncation(el);
          }}
        >
          {scene.comment}
          <span>
            <IconButton
              className={classes.infoIcon}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setDialogTitle("Comment");
                setDialogText(scene.comment);
                setDialogOpen(true);
              }}
              tabIndex={-1}
            >
              <InfoOutlinedIcon fontSize="inherit" />
            </IconButton>
          </span>
        </div>
      )}
    </div>
  );
};

SceneImagePreview.propTypes = {
  scene: PropTypes.object.isRequired,
  showAdditionalName: PropTypes.bool,
  showComments: PropTypes.bool,
  fileStatus: PropTypes.string.isRequired,
  isTruncated: PropTypes.func.isRequired,
  setDialogTitle: PropTypes.func.isRequired,
  setDialogText: PropTypes.func.isRequired,
  setDialogOpen: PropTypes.func.isRequired,
  handleImageError: PropTypes.func.isRequired,
  width: PropTypes.string,
  scrollContainer: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  useAdditionalNameInsteadOfFilename: PropTypes.bool,
};

// set default props for optional fields
SceneImagePreview.defaultProps = {
  showAdditionalName: false,
  showComments: false,
  fileStatus: "",
  isTruncated: () => false,
  setDialogTitle: () => {},
  setDialogText: () => {},
  setDialogOpen: () => {},
  handleImageError: () => {},
  scrollContainer: undefined,
  useAdditionalNameInsteadOfFilename: false,
};

export default SceneImagePreview;
